import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	formatPhoneNumber,
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type WelcomePopup } from '@/scripts/theme/welcome-popup'
import { motionEnabled } from '@/scripts/core/motion'

export class KlaviyoForm extends UcoastEl {
	static htmlSelector = 'klaviyo-form'
	form: HTMLFormElement
	submitButton: HTMLButtonElement
	emailInput: HTMLInputElement
	successMessage: string
	errorMessage: string
	loadingMessage: string
	route: string
	popup?: WelcomePopup
	termsElements?: {
		termsAccepted: HTMLButtonElement
		termsLabel: HTMLLabelElement
	}
	termsAccepted = true
	companyId: string
	isSubmitting = false
	progressiveNextButton?: HTMLButtonElement
	phoneInput?: HTMLInputElement

	constructor() {
		super()
		this.form = q.rs('form', this)
		this.submitButton = q.rs('[type="submit"]', this)
		this.emailInput = q.rs('[type="email"]', this)
		this.successMessage = this.getSuccessMessage()
		this.errorMessage = q.ra(this.form, 'data-error')
		this.loadingMessage = q.ra(this.form, 'data-loading')
		this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
		this.popup = q.os<WelcomePopup>('welcome-popup')
		this.termsElements = this.getTermsElements()
		this.termsSetup()
		this.progressiveNextButton = q.os<HTMLButtonElement>(
			'[data-progressive-next]',
			this
		)
		this.phoneInput = q.os<HTMLInputElement>('[type="tel"]', this)
		this.progressiveNextButton?.addEventListener(
			'click',
			this.showProgressivePhoneStep
		)
		this.companyId = q.ra(this, 'data-company-id')
		this.route = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.companyId}`
	}

	showProgressivePhoneStep = () => {
		if (!this.emailInput.reportValidity()) return

		const showPhoneStep = () => {
			this.querySelectorAll<HTMLElement>('[data-progressive-step]').forEach(
				(element) => {
					element.hidden = element.dataset.progressiveStep !== 'phone'
				}
			)
		}

		if (!motionEnabled()) {
			showPhoneStep()
			this.phoneInput?.focus()
			return
		}

		this.form.classList.add('is-changing-step')
		let finished = false
		let fallback: number | undefined
		const finishStepChange = () => {
			if (finished) return
			finished = true
			this.form.removeEventListener('transitionend', onTransitionEnd)
			if (fallback !== undefined) window.clearTimeout(fallback)
			showPhoneStep()
			window.requestAnimationFrame(() => {
				this.form.classList.remove('is-changing-step')
				this.phoneInput?.focus()
			})
		}
		const onTransitionEnd = (event: TransitionEvent) => {
			if (event.target === this.form && event.propertyName === 'opacity') {
				finishStepChange()
			}
		}
		this.form.addEventListener('transitionend', onTransitionEnd)
		fallback = window.setTimeout(finishStepChange, 220)
	}

	getTermsElements() {
		const termsAccepted = q.os<HTMLButtonElement>(
			'button[data-terms-accepted]',
			this
		)
		const termsLabel = q.os<HTMLLabelElement>(
			'[data-terms-label]',
			this
		)
		if (termsAccepted && termsLabel) {
			return {
				termsAccepted,
				termsLabel,
			}
		}
		return undefined
	}

	termsSetup() {
		if (!this.termsElements) return
		this.termsAccepted = false
		this.termsElements.termsAccepted.addEventListener(
			'click',
			this.toggleTerms.bind(this)
		)
		this.termsElements.termsLabel.addEventListener(
			'click',
			this.toggleTerms.bind(this)
		)
	}

	toggleTerms() {
		if (!this.termsElements) throw new Error('Terms elements not found')
		if (this.termsAccepted) {
			this.termsAccepted = false
			this.termsElements.termsAccepted.classList.remove('active')
			this.submitButton.setAttribute('disabled', '')
		} else {
			this.termsElements.termsAccepted.classList.add('active')
			this.submitButton.removeAttribute('disabled')
			this.termsAccepted = true
		}
	}

	getSuccessMessage(): string {
		const message = this.form.getAttribute('data-success')
		return message ?? 'Thank You'
	}

	showStatus(
		message: string,
		state: 'loading' | 'success' | 'error'
	) {
		const status = q.os<HTMLElement>('[data-form-status]', this)
		if (!status) return

		const isError = state === 'error'
		const headingId = this.form.dataset.successHeading
		if (state === 'success' && headingId) {
			const heading = document.getElementById(headingId)
			if (heading) {
				heading.textContent = message
				if (
					getComputedStyle(heading).visibility !== 'hidden' &&
					(this.contains(document.activeElement) || document.activeElement === document.body)
				) {
					heading.focus({ preventScroll: true })
				}
				message = this.form.dataset.successBody ?? message
			}
		}
		status.textContent = message
		status.classList.toggle('klaviyo-form__message--error', isError)
		status.classList.toggle(
			'klaviyo-form__message--success',
			state === 'success'
		)
		status.classList.toggle(
			'klaviyo-form__message--loading',
			state === 'loading'
		)
		status.classList.toggle(
			'newsletter-form__message--success',
			state === 'success'
		)
		status.setAttribute('role', isError ? 'alert' : 'status')
		status.setAttribute('aria-live', isError ? 'assertive' : 'polite')
		status.classList.remove('hidden')
		this.dataset.state = state
	}

	onSubmitHandler(event: Event) {
		event.preventDefault()
		if (this.isSubmitting || !this.termsAccepted) return
		if (this.emailInput.value.length < 1) return

		this.isSubmitting = true
		this.form.setAttribute('aria-busy', 'true')
		this.emailInput.removeAttribute('aria-invalid')
		this.emailInput.closest('.field')?.classList.remove('field--with-error')
		this.submitButton.disabled = true
		this.submitButton.setAttribute('aria-disabled', 'true')
		this.submitButton.setAttribute('aria-busy', 'true')
		this.submitButton.classList.add('loading')
		this.showStatus(this.loadingMessage, 'loading')

		const formData = new FormData(this.form)

		const config = KlaviyoForm.addToKlaviyoListConfig(formData)

		fetch(this.route, config)
			.then(async (response) => {
				const responseBody = await response.text()
				if (!response.ok) {
					throw new Error(responseBody || `Klaviyo request failed: ${response.status}`)
				}
			})
			.then(() => {
				this.form.reset()
				this.showStatus(this.successMessage, 'success')
				if (this.popup) {
					window.setTimeout(() => {
						this.popup?.hide()
					}, 1000)
				}
			})
			.catch((e) => {
				console.error(e)
				this.emailInput.setAttribute('aria-invalid', 'true')
				this.emailInput.closest('.field')?.classList.add('field--with-error')
				this.showStatus(this.errorMessage, 'error')
			})
			.finally(() => {
				this.isSubmitting = false
				this.form.removeAttribute('aria-busy')
				this.submitButton.disabled = !this.termsAccepted
				this.submitButton.classList.remove('loading')
				this.submitButton.removeAttribute('aria-busy')
				this.submitButton.removeAttribute('aria-disabled')
			})
	}

	static addToKlaviyoListConfig(body: FormData) {
		const values = {
			email: q.rfd(body, 'email'),
			list_id: q.rfd(body, 'list_id'),
			custom_source: q.rfd(body,'custom_source'),
			phone_number: q.ofd(body, 'phone_number'),
			language_iso: q.ofd(body, 'language_iso') ?? 'en',
			market_handle: q.ofd(body, 'market_handle') ?? 'us'
		}
		if (values.phone_number) {
			values.phone_number = formatPhoneNumber(values.phone_number)
		}
		const data ={
			data: {
				type: "subscription",
				attributes: {
					custom_source: values.custom_source,
					profile: {
						data: {
							type: "profile",
							attributes: {
								email: values.email,
								phone_number: values.phone_number,
								properties: {
									language_iso: values.language_iso,
									market_handle: values.market_handle,
								}
							},
						}
					}
				},
				relationships: {
					list: {
						data: {
							type: "list",
							id: values.list_id
						}
					}
				}
			}
		}
		return {
			method: 'POST',
			mode: 'cors' as RequestMode,
			headers: {
				'Content-Type': 'application/json',
				'Revision': '2026-07-15'
			},
			body: JSON.stringify(data),
		}
	}
}
